import { type ReactElement, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { routePaths } from '../app/route-paths.js';
import { Button } from '../components/ui/Button.js';
import { errorText } from '../components/ui/error-text.js';
import { FormError } from '../components/ui/FormError.js';
import { Spinner } from '../components/ui/Spinner.js';
import { useActiveFamily } from '../hooks/useActiveFamily.js';
import { useAcceptInvitation, useInvitationPreview } from '../hooks/useInvitations.js';

const statusCopy: Record<string, string> = {
    pending: 'You can join this family with the role below.',
    accepted: 'This invitation has already been used.',
    revoked: 'This invitation was revoked.',
    expired: 'This invitation has expired.',
};

export function InvitePage(): ReactElement {
    const params = useParams();
    const token = params.token ?? '';
    const navigate = useNavigate();
    const preview = useInvitationPreview(token);
    const accept = useAcceptInvitation(token);
    const { setActiveFamilyId } = useActiveFamily();
    const [serverError, setServerError] = useState<string | null>(null);

    if (preview.isPending) {
        return <Spinner label="Loading invitation" />;
    }
    if (preview.isError) {
        return (
            <>
                <h1>Invitation unavailable</h1>
                <FormError message={errorText(preview.error)} />
            </>
        );
    }

    const invitation = preview.data;
    async function onAccept(): Promise<void> {
        setServerError(null);
        try {
            const result = await accept.mutateAsync();
            setActiveFamilyId(result.familyId);
            await navigate(routePaths.today);
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    return (
        <>
            <h1>Join {invitation.familyName}</h1>
            <p>{statusCopy[invitation.status]}</p>
            <p>
                Role: {invitation.role === 'caregiver' ? 'Caregiver' : 'Viewer'}
                {invitation.email ? `. Only ${invitation.email} can accept.` : ''}
            </p>
            <FormError message={serverError} />
            {invitation.status === 'pending' ? (
                <Button
                    loading={accept.isPending}
                    onClick={() => {
                        void onAccept();
                    }}
                >
                    Accept invitation
                </Button>
            ) : null}
        </>
    );
}
